import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { Tabs } from '@ipfabric/ui-components/Tabs/Tabs';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import { safeAbsurd } from '@ipfabric/utils';
import { NotFoundPage } from 'pages/NotFoundPage/NotFoundPage';
import { useEffect, useState } from 'react';
import { AssignedLabelsTabContent } from './AssignedLabelsTabContent';
import { LabelsTabContent } from './Labels/LabelsTabContent';
import { RulesTabContent } from './RulesTabContent';
import { useDiscoveryDataEnrichmentLink } from './useDiscoveryDataEnrichmentLink';

type DiscoveryDataEnrichmentTab = 'labels' | 'rules' | 'assignedLabels';

const tabs = [
  { id: 'labels' as const, label: 'Labels' },
  { id: 'rules' as const, label: 'Rules' },
  { id: 'assignedLabels' as const, label: 'Assigned labels' },
];

// Gate on ENABLE_LABELS here (render time) rather than when building the route
// tree (module-eval time), where feature flags are not yet loaded.
export const DiscoveryDataEnrichmentPage = () => {
  if (!isFeatureFlagEnabled('ENABLE_LABELS')) {
    return <NotFoundPage />;
  }

  return <DiscoveryDataEnrichmentContent />;
};

const DiscoveryDataEnrichmentContent = () => {
  const handedOverLink = useDiscoveryDataEnrichmentLink();
  const [link, setLink] = useState(handedOverLink);
  const [tab, setTab] = useState<DiscoveryDataEnrichmentTab>(
    handedOverLink?.tab ?? 'labels',
  );

  // a tab can hand over to another one, e.g. from an assignment to the rule
  // that produced it
  useEffect(() => {
    if (handedOverLink === null) {
      return;
    }

    setLink(handedOverLink);
    setTab(handedOverLink.tab);
  }, [handedOverLink]);

  // switching tabs by hand leaves the hand-off behind, so coming back to the
  // tab does not filter it again
  const changeTab = (next: DiscoveryDataEnrichmentTab) => {
    setLink(null);
    setTab(next);
  };

  return (
    <>
      <PageHeader elevated width='full' label='Discovery Data Enrichment' />
      <div className='flex-1 m flex flex-col'>
        <div className='flex flex-col flex-1 drop-shadow min-h-0'>
          <Tabs tabs={tabs} value={tab} onChange={changeTab} variant='outer' />
          <div className='flex flex-1 w-full'>
            {(() => {
              switch (tab) {
                case 'labels':
                  return <LabelsTabContent />;
                case 'rules':
                  return (
                    <RulesTabContent
                      initialRuleId={
                        link?.tab === 'rules' ? link.ruleId : undefined
                      }
                    />
                  );
                case 'assignedLabels':
                  return (
                    <AssignedLabelsTabContent
                      initialRootId={
                        link?.tab === 'assignedLabels' ? link.rootId : undefined
                      }
                    />
                  );
                default:
                  safeAbsurd(tab);
                  return null;
              }
            })()}
          </div>
        </div>
      </div>
    </>
  );
};
